import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { hasScrollTicket } from '@/lib/browseScroll';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * GLOBAL SCROLL POSITION ON NAVIGATION.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * THE DEFECT THIS FIXES
 * React Router does not reset the scroll position when the route changes — the
 * document simply keeps the offset it already had. So a visitor who read to the
 * bottom of the home page and then pressed "Pricing" in the footer arrived on
 * the Pricing page already scrolled ~2,000px down, somewhere in the FAQ, with
 * the plan cards above them and no indication that anything had loaded. The
 * same happened for Membership, Dashboard, Legal, Contact and every programmatic
 * SEO page: the taller the page you left, the further down you landed.
 *
 * THE RULE
 * A new page opens at the top. Always. Three deliberate exceptions, each one a
 * case where jumping to the top would DESTROY something the user expects:
 *
 *   1. POP (browser Back / Forward) — the browser restores the previous offset
 *      itself. Overriding it would break "press Back and carry on reading".
 *   2. A return-to-browse ticket is waiting — the contractor is coming back to
 *      the marketplace results they were just in, and Jobs.tsx restores their
 *      exact card position one frame after the grid paints
 *      (see src/lib/browseScroll.ts, the single source of truth for that).
 *   3. The language toggle — /en/pricing -> /es/precios is the SAME page in the
 *      other language, so the reader keeps their place instead of being thrown
 *      back to the hero.
 *
 * Anchors (#leave-a-review) are honoured: the element is scrolled into view
 * after layout rather than the page being forced to the top, so "Leave a
 * Review" style CTAs land on the form from any page.
 *
 * Renders nothing.
 */

/** '/es/precios' -> 'es'. Lets us spot a pure language switch. */
const langOf = (pathname: string) => {
  const seg = pathname.split('/')[1];
  return seg === 'en' || seg === 'es' ? seg : '';
};


const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    // 1. Back / Forward: the browser owns the scroll position.
    if (navType === 'POP') return;

    // 3. Language toggle. It is the only navigation that REPLACES the history
    //    entry while changing the /en <-> /es prefix (see setLang in
    //    src/i18n/LanguageContext), and the route slug itself is localised
    //    (/en/pricing -> /es/precios), so comparing prefixes is the reliable
    //    signature. Same page, other language: the reader keeps their place
    //    instead of being thrown back to the hero.
    const previous = (window as { __ggLastPath?: string }).__ggLastPath || '';
    (window as { __ggLastPath?: string }).__ggLastPath = pathname;
    if (navType === 'REPLACE' && previous && langOf(previous) !== langOf(pathname)) {
      return;
    }

    if (hash) {
      const id = hash.slice(1);
      window.requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo(0, 0);
      });
      return;
    }

    // 2. Deliberate return to browse results — Jobs.tsx restores the position.
    if (hasScrollTicket()) return;

    window.scrollTo(0, 0);
  }, [pathname, hash, navType]);

  return null;
};

export default ScrollToTop;
